import React, { useMemo, useState } from 'react';
import { Star, Repeat, Ban, Plus, X, GripVertical, Sparkles, Lightbulb } from 'lucide-react';
import { POOL_STATUS, buildPoolView, setChampionStatus, getUnplayedPoolChampions } from '../lib/championPool';
import { getChampionIconUrl } from '../lib/champions';
import ChampionSelect from './ChampionSelect';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';

/**
 * V6.8 — Manager du pool de champions
 *
 * Affiche 4 zones : Mains / Flex / Mis de côté / Non classés (zone implicite)
 * Drag-and-drop entre zones via HTML5
 * Sélecteur pour ajouter un champion non joué dans Mains
 */

const ZONE_ICONS = {
  main: Star,
  flex: Repeat,
  sidelined: Ban
};

function ChampionCard({ champion, onDragStart, version, isDraggable = true, onContextMenu, onClick }) {
  const winrateColor = champion.winrate === null
    ? 'text-zinc-500'
    : champion.winrate >= 54 ? 'text-cyan-400' : champion.winrate < 45 ? 'text-rose-400' : 'text-amber-400';

  return (
    <div
      draggable={isDraggable}
      onDragStart={onDragStart}
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`flex items-center gap-2 p-2 border bg-zinc-950 hover:bg-zinc-900 transition group ${
        isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
      } ${champion.status === 'sidelined' ? 'opacity-60' : ''}`}
      style={{
        borderColor: champion.isUnplayed ? '#a78bfa55' : '#3f3f4655'
      }}
      title={isDraggable ? 'Glisser pour déplacer · Clic-droit pour menu' : champion.name}
    >
      {isDraggable && <GripVertical size={11} className="text-zinc-700 group-hover:text-zinc-500 flex-shrink-0" />}
      <img
        src={getChampionIconUrl(champion.name, version)}
        alt={champion.name}
        className={`w-8 h-8 object-cover flex-shrink-0 ${champion.status === 'sidelined' ? 'grayscale' : ''}`}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-bold truncate ${champion.status === 'sidelined' ? 'line-through text-zinc-500' : 'text-white'}`}>
          {champion.name}
        </div>
        <div className="text-[10px] font-mono text-zinc-500">
          {champion.isUnplayed ? (
            <span className="text-violet-400">📚 À apprendre</span>
          ) : (
            <>
              {champion.games}G · <span className={winrateColor}>{champion.winrate !== null ? `${champion.winrate}%` : '—'}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Zone({ zoneId, label, icon: Icon, color, desc, champions, onDrop, onDragOver, version, onChampionMenu }) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        onDrop(zoneId, e);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
        onDragOver?.(e);
      }}
      onDragLeave={() => setIsDragOver(false)}
      className={`border bg-zinc-950 transition relative overflow-hidden ${isDragOver ? 'border-2' : ''}`}
      style={{
        borderColor: isDragOver ? color : '#27272a',
        boxShadow: isDragOver ? `0 0 16px -4px ${color}80, inset 0 0 16px -8px ${color}40` : 'none'
      }}
    >

      <div className="border-b border-zinc-800 p-3 flex items-center gap-2">
        <Icon size={14} style={{ color }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color }}>
            {label}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono">{desc}</div>
        </div>
        <div className="text-xs font-mono font-bold" style={{ color }}>
          {champions.length}
        </div>
      </div>
      <div className="p-2 space-y-1.5 min-h-[80px]">
        {champions.length === 0 ? (
          <div className="text-[10px] text-zinc-600 font-mono italic text-center py-4">
            {isDragOver ? '↓ Lâcher ici' : 'Vide — glisse un champion ici'}
          </div>
        ) : (
          champions.map(champ => (
            <ChampionCard
              key={champ.name}
              champion={champ}
              version={version}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', champ.name);
                e.dataTransfer.setData('source-zone', zoneId);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                onChampionMenu?.(champ, e);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function ChampionPoolManager({ games, userSettings, champions, version, onUpdatePool, onSuggestObjective }) {
  // v7.2.1 : safeguard contre champion_pool non-array
  const poolRaw = userSettings?.champion_pool;
  const pool = Array.isArray(poolRaw) ? poolRaw : [];
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedChamp, setSelectedChamp] = useState('Caitlyn');
  const [contextMenu, setContextMenu] = useState(null); // { champion, x, y }

  const zones = useMemo(() => buildPoolView(games, pool), [games, pool]);

  const handleDrop = async (targetZone, e) => {
    const championName = e.dataTransfer.getData('text/plain');
    const sourceZone = e.dataTransfer.getData('source-zone');
    if (!championName || sourceZone === targetZone) return;

    // Si cible = unclassified : on retire du pool
    const newStatus = targetZone === 'unclassified' ? null : targetZone;
    const newPool = setChampionStatus(pool, championName, newStatus, 'manual');
    await onUpdatePool(newPool);
  };

  const handleAddUnplayed = async () => {
    if (!selectedChamp) return;
    // Vérif : le champion ne doit pas déjà être dans le pool
    const exists = pool.some(p => p.name.toLowerCase() === selectedChamp.toLowerCase());
    if (exists) {
      alert(`${selectedChamp} est déjà dans ton pool`);
      return;
    }
    const hasGames = games.some(g => g.champion?.toLowerCase() === selectedChamp.toLowerCase());
    const newPool = setChampionStatus(pool, selectedChamp, 'main', hasGames ? 'manual' : 'manual_unplayed');
    await onUpdatePool(newPool);
    setShowAddModal(false);

    // Si nouveau champion non joué → trigger suggestion d'objectif
    if (!hasGames && onSuggestObjective) {
      onSuggestObjective({
        type: 'new_unplayed_champion',
        champion: selectedChamp
      });
    }
  };

  const handleContextMenuChange = async (newStatus) => {
    if (!contextMenu) return;
    const newPool = setChampionStatus(pool, contextMenu.champion.name, newStatus, 'manual');
    await onUpdatePool(newPool);
    setContextMenu(null);
  };

  return (
    <div className="space-y-4">
      {/* Header + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-semibold flex items-center gap-2">
              <Star size={11} className="text-cyan-400" /> Champion Pool
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">Glisse-dépose entre zones · clic-droit pour menu rapide</div>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black px-3 py-1.5 font-black text-xs tracking-wider hover:opacity-90 transition flex items-center gap-1.5"
        >
          <Plus size={12} /> AJOUTER
        </button>
      </div>

      {/* Grid 3 zones principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Zone
          zoneId="main"
          label={POOL_STATUS.main.label}
          icon={Star}
          color={POOL_STATUS.main.color}
          desc={POOL_STATUS.main.desc}
          champions={zones.main}
          onDrop={handleDrop}
          version={version}
          onChampionMenu={(champ, e) => setContextMenu({ champion: champ, x: e.clientX, y: e.clientY })}
        />
        <Zone
          zoneId="flex"
          label={POOL_STATUS.flex.label}
          icon={Repeat}
          color={POOL_STATUS.flex.color}
          desc={POOL_STATUS.flex.desc}
          champions={zones.flex}
          onDrop={handleDrop}
          version={version}
          onChampionMenu={(champ, e) => setContextMenu({ champion: champ, x: e.clientX, y: e.clientY })}
        />
        <Zone
          zoneId="sidelined"
          label={POOL_STATUS.sidelined.label}
          icon={Ban}
          color={POOL_STATUS.sidelined.color}
          desc={POOL_STATUS.sidelined.desc}
          champions={zones.sidelined}
          onDrop={handleDrop}
          version={version}
          onChampionMenu={(champ, e) => setContextMenu({ champion: champ, x: e.clientX, y: e.clientY })}
        />
      </div>

      {/* Zone "Non classés" — implicite, en accordéon ou en ligne */}
      {zones.unclassified.length > 0 && (
        <div className="border border-zinc-800 bg-zinc-950">
          <div
            onDrop={(e) => {
              e.preventDefault();
              handleDrop('unclassified', e);
            }}
            onDragOver={(e) => e.preventDefault()}
            className="p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={12} className="text-zinc-500" />
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold flex-1">
                Joués mais non classés ({zones.unclassified.length})
              </div>
              <div className="text-[10px] text-zinc-600 font-mono italic">Glisse vers une zone pour classer</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1.5">
              {zones.unclassified.map(champ => (
                <ChampionCard
                  key={champ.name}
                  champion={champ}
                  version={version}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', champ.name);
                    e.dataTransfer.setData('source-zone', 'unclassified');
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ champion: champ, x: e.clientX, y: e.clientY });
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-zinc-950 border border-cyan-400/30 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-cyan-400" />
                <div className="font-black text-base">Ajouter au pool</div>
              </div>
              <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="text-xs text-zinc-400 mb-3">
              Sélectionne un champion à ajouter dans tes Mains. Si tu ne l'as jamais joué, l'app te proposera un objectif d'apprentissage.
            </div>
            <ChampionSelect
              value={selectedChamp}
              onChange={setSelectedChamp}
              champions={champions}
              version={version}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-zinc-800 text-zinc-400 p-2.5 hover:border-zinc-700 transition font-bold tracking-wider text-xs"
              >
                ANNULER
              </button>
              <button
                onClick={handleAddUnplayed}
                className="flex-1 bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black p-2.5 font-black tracking-wider text-xs hover:opacity-90 transition"
              >
                AJOUTER EN MAIN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context menu rapide */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
          <div
            className="fixed z-50 bg-zinc-950 border border-cyan-400/40 shadow-xl py-1 min-w-[160px]"
            style={{ left: Math.min(contextMenu.x, window.innerWidth - 180), top: Math.min(contextMenu.y, window.innerHeight - 200) }}
          >
            <div className="px-3 py-2 border-b border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold truncate">
              {contextMenu.champion.name}
            </div>
            <button
              onClick={() => handleContextMenuChange('main')}
              className="w-full text-left px-3 py-2 hover:bg-cyan-400/10 transition flex items-center gap-2 text-xs"
            >
              <Star size={11} className="text-cyan-400" /> <span className="text-white">Main</span>
            </button>
            <button
              onClick={() => handleContextMenuChange('flex')}
              className="w-full text-left px-3 py-2 hover:bg-violet-400/10 transition flex items-center gap-2 text-xs"
            >
              <Repeat size={11} className="text-violet-400" /> <span className="text-white">Flex</span>
            </button>
            <button
              onClick={() => handleContextMenuChange('sidelined')}
              className="w-full text-left px-3 py-2 hover:bg-rose-400/10 transition flex items-center gap-2 text-xs"
            >
              <Ban size={11} className="text-zinc-400" /> <span className="text-white">Mis de côté</span>
            </button>
            {contextMenu.champion.status && (
              <>
                <div className="border-t border-zinc-800 my-1" />
                <button
                  onClick={() => handleContextMenuChange(null)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-800 transition text-xs text-zinc-500"
                >
                  Retirer du pool
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
